import styled, { css, ThemedStyledFunction } from 'styled-components';
import { FC, MouseEventHandler } from 'react';
import { Link as ReactRouterLink, useLocation } from 'react-router-dom';
import { UIComponentProps } from '../../styles/UIComponentProps';
import { useGA } from '../../utils/tracking/useGA';
import { useNavigationRoot } from '../../utils/context/AppRootContext';

interface LinkProps {
  href: string;
  external?: boolean;
  state?: {} & any;
  onClick?: MouseEventHandler;
  download?: string;
  target?: string;
  plainLink?: boolean;
}

const LinkBase = (base: ThemedStyledFunction<any, any>) => base.attrs(({ external = false, href }: LinkProps) => ({
  ...(external ? {
    target: '_blank',
  } : {}),
  ...(!href?.startsWith('/') ? {
    rel: 'noopener noreferrer nofollow',
  } : {}),
}))<LinkProps>`
  color: inherit;
  text-decoration: inherit;

  ${({ href = '' }) => href.length < 1 && css`
    pointer-events: none;
  `};
`;

const Link = LinkBase(styled.a);
const StyledRouterLink = LinkBase(styled(ReactRouterLink));

const InternalLink: FC<LinkProps & UIComponentProps> = (props) => {
  const location = useLocation();

  return (
    <StyledRouterLink
      {...props}
      to={{ pathname: props.href, state: props.state || {} }}
      onClick={(e: any) => {
        const [href] = `${props.href}?`.split('?');
        if (location.pathname === href) {   // scroll to top if links to same page
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        }

        if (props.onClick) {
          props.onClick(e);
        }
      }}
    >
      {props.children}
    </StyledRouterLink>
  );
};

export const LinkHandler: FC<LinkProps & UIComponentProps> = (props) => {
  const {
    href,
    external,
    target,
    plainLink = false,
  } = props;

  const { trackEvent } = useGA();
  const navigationRoot = useNavigationRoot();

  const additionalProps: any = {
    onClick: (event: any) => {
      trackEvent({
        category: 'click',
        action: `clicked on link: ${href}`,
      });

      if (props.onClick) {
        props.onClick(event);
      }
    },
  };

  if (href?.startsWith('/') && !external && !plainLink) {
    return (
      <InternalLink {...props} {...additionalProps} />
    );
  }

  return (
    <Link
      {...props}
      href={href?.startsWith('/') && navigationRoot !== '/' ? `${navigationRoot}${href}` : href}
      {...additionalProps}
      target={target}
    />
  );
};